import { LayoutOrientation } from '@cadet-dsl';
import { AdaptiveColor, ColorName, getAdaptiveColor } from '@color';
import { Point, Rect, rect, Size } from '@geom';
import { topologicalSort, type NID } from '@graph';
import { localeManager, prefs } from '@reactive';
import { createEntityLayoutSpec, layoutEntity, type EntitySpec } from '@snorf';
import { LocaleId, StringId } from '@strings';
import { FixedFontSizeChooser, layoutText } from '@typography';
import { expectDefined } from '@utils';
import { VisualCache } from '@visual';
import Cytoscape from 'cytoscape';
import { Rectangle as CoRectangle, removeOverlaps as coRemoveOverlaps } from 'webcola';
import { EdgeRouter } from '../layout/EdgeRouter';
import { LayoutConstants } from '../layout/LayoutConstants';
import {
  colaIncrementalLayoutOptions,
  colaInitialLayoutOptions,
  type AlignmentConstraint,
  type AlignmentConstraints,
  type GapInequalityConstraint
} from '../layout/colaUtils';
import type { Doc } from './Doc';
import { EdgeLayout } from './EdgeLayout';
import { GroupLayout } from './GroupLayout';
import type { GroupSpec } from './GroupSpec';
import { UnitLayout } from './UnitLayout';
import { UnitSpec } from './UnitSpec';
import { cytoscapeToDoc, docToCytoscape } from './cyUtils';

export class Layouter {
  cy: Cytoscape.Core;
  cache: VisualCache;

  constructor(cy: Cytoscape.Core) {
    this.cy = cy;
    this.cache = new VisualCache();
  }

  dispose() {
    this.cache.clear();
    this.cy.destroy();
  }

  constraints(doc: Doc): {
    gapInequalities?: GapInequalityConstraint[];
    alignment?: AlignmentConstraints;
  } {
    const layoutOrientation = doc.orientation;
    if (layoutOrientation === LayoutOrientation.UNCONSTRAINED) {
      return {};
    }

    let axis: 'x' | 'y';
    switch (layoutOrientation) {
      case LayoutOrientation.LEFT_TO_RIGHT:
      case LayoutOrientation.RIGHT_TO_LEFT:
        axis = 'x';
        break;
      case LayoutOrientation.BOTTOM_TO_TOP:
      case LayoutOrientation.TOP_TO_BOTTOM:
        axis = 'y';
        break;
    }

    let gapInequalities: GapInequalityConstraint[] | undefined = [];
    const graph = doc.graph;
    for (const edge of graph.edges) {
      let source: NID;
      let target: NID;
      switch (layoutOrientation) {
        case LayoutOrientation.LEFT_TO_RIGHT:
        case LayoutOrientation.TOP_TO_BOTTOM:
          source = graph.source(edge);
          target = graph.target(edge);
          break;
        case LayoutOrientation.RIGHT_TO_LEFT:
        case LayoutOrientation.BOTTOM_TO_TOP:
          source = graph.target(edge);
          target = graph.source(edge);
          break;
      }
      const sourceSize = doc.nodeLayout(source).bounds.width;
      const targetSize = doc.nodeLayout(target).bounds.width;
      const gap =
        sourceSize / 2 + targetSize / 2 + LayoutConstants.EDGE_INEQUALITY_CONSTRAINT_GAP;
      const left = this.cy.getElementById(source);
      const right = this.cy.getElementById(target);
      gapInequalities.push({ axis, left, right, gap });
    }

    let alignment: AlignmentConstraints | undefined = {
      vertical: [],
      horizontal: []
    };

    const alignRoots = doc.alignRoots;
    const alignLeaves = doc.alignLeaves;
    if (alignRoots || alignLeaves) {
      const rootsToAlign: NID[] = [];
      const leavesToAlign: NID[] = [];

      const graph = doc.graph;
      for (const node of graph.nodes) {
        if (doc.isGroup(node)) {
          continue;
        }
        const isRoot = graph.graph.isRoot(node);
        const isLeaf = graph.graph.isLeaf(node);
        if (isRoot && alignRoots) {
          rootsToAlign.push(node);
        } else if (isLeaf && alignLeaves) {
          leavesToAlign.push(node);
        }
      }

      if (rootsToAlign.length > 0 || leavesToAlign.length > 0) {
        const rootAlignments: AlignmentConstraint[] = rootsToAlign.map(node => ({
          node: this.cy.getElementById(node),
          offset: 0
        }));
        const leafAlignments: AlignmentConstraint[] = leavesToAlign.map(node => ({
          node: this.cy.getElementById(node),
          offset: 0
        }));

        const alignments: AlignmentConstraint[][] = [];
        if (rootsToAlign.length >= 2) {
          alignments.push(rootAlignments);
        }
        if (leavesToAlign.length >= 2) {
          alignments.push(leafAlignments);
        }
        switch (layoutOrientation) {
          case LayoutOrientation.LEFT_TO_RIGHT:
          case LayoutOrientation.RIGHT_TO_LEFT:
            alignment = { vertical: alignments, horizontal: [] };
            break;
          case LayoutOrientation.TOP_TO_BOTTOM:
          case LayoutOrientation.BOTTOM_TO_TOP:
            alignment = { vertical: [], horizontal: alignments };
            break;
        }
      }
    }

    if (gapInequalities.length === 0) {
      gapInequalities = undefined;
    }

    if (alignment.vertical.length === 0 && alignment.horizontal.length === 0) {
      alignment = undefined;
    }

    return { gapInequalities, alignment };
  }

  runInitialLayout(doc: Doc): Doc {
    const result = doc.produce('initialLayout', draft => {
      const d = draft as Doc;
      layoutNodes(d, this.cache);
      docToCytoscape(d, this.cy);
      const layoutOptions = {
        ...colaInitialLayoutOptions(),
        nodeSpacing: (node: { id: () => NID }) => (d.isGroup(node.id()) ? 10 : 50),
        ...this.constraints(d)
        // userConstIter: 100,
      };
      const layout = this.cy.makeLayout(
        layoutOptions as unknown as Cytoscape.LayoutOptions
      );

      console.log('📦 🟢 runInitialLayout layout.run Start');
      layout.run();
      console.log('📦 🔴 runInitialLayout layout.run End');

      cytoscapeToDoc(this.cy, d);
    });

    return result;
  }

  runIncrementalLayout(doc: Doc, _lastBounds: Rect | undefined): Doc {
    docToCytoscape(doc, this.cy);
    const layoutOptions = {
      ...colaIncrementalLayoutOptions(),
      nodeSpacing: (node: { id: () => NID }) => (doc.isGroup(node.id()) ? 10 : 50),
      ...this.constraints(doc),
      userConstIter: 100
    };
    const layout = this.cy.makeLayout(
      layoutOptions as unknown as Cytoscape.LayoutOptions
    );

    // console.log('📦 🟢 runIncrementalLayout layout.run Start');
    layout.run();
    // console.log('📦 🔴 runIncrementalLayout layout.run End');

    const result = doc.produce('incrementalLayout', draft => {
      const d = draft as Doc;
      cytoscapeToDoc(this.cy, d);
      removeOverlaps(d);
      // recenterNodes(d, lastBounds);
      layoutEdges(d);
      d.hasLayout = true;
    });

    return result;
  }
}

function layoutNodes(doc: Doc, cache: VisualCache) {
  for (const node of doc.graph.nodes) {
    layoutNode(doc, node, cache);
  }
}

// Recenters the nodes in the newly laid out graph to halfway to the center of
// the previous graph.
function _recenterNodes(doc: Doc, initalBounds?: Rect) {
  const newBounds = doc.bounds;
  if (newBounds === undefined || initalBounds === undefined) {
    return;
  }

  const newCenter = newBounds.center;
  const initialCenter = initalBounds.center;
  const delta = initialCenter.sub(newCenter).div(2);

  doc.offsetNodes(delta);
}

/**
 * Do a depth-first traversal of the graph and remove overlaps between nodes
 * at every level of the hierarchy. This is necessary because the webcola
 * algorithm does not guarantee that nodes will not overlap, but the MSAGL
 * edge routing algorithm requires that nodes do not overlap.
 */
function removeOverlaps(draft: Doc, parent?: NID) {
  // Get the children of the parent node. If there is no parent, get the roots of the forest.
  const children = Array.from(
    parent !== undefined ? draft.graph.children(parent) : draft.graph.forest.roots
  );

  // If the parent has no children, return.
  if (children.length === 0) {
    return;
  }

  // Recursively remove overlaps in the children before removing overlaps in the present level.
  for (const child of children) {
    if (draft.graph.hasChildren(child)) {
      removeOverlaps(draft, child);
    }
  }

  // Remove overlaps in the present level.
  const childBounds = children.map(child => draft.nodeBounds(child));
  const childBoundsUnion = Rect.fromRects(childBounds);
  const padding = 10;
  const childBoundsPadded = childBounds.map(bounds => bounds.expandingBy(padding));
  const rs = childBoundsPadded.map(
    bounds => new CoRectangle(bounds.left, bounds.right, bounds.top, bounds.bottom)
  );
  coRemoveOverlaps(rs);
  const newChildBounds = rs.map(r => rect(r.x, r.y, r.X, r.Y));
  const newChildBoundsUnion = Rect.fromRects(newChildBounds);
  children.forEach((child, i) => {
    const offset = newChildBounds[i].topLeft.sub(childBounds[i].topLeft);
    if (offset.isZero) {
      return;
    }
    draft.offsetNodeAndDescendants(child, offset);
  });

  // Adjust the parent bounds if necessary.
  if (parent !== undefined) {
    // Removing overlaps will only ever increase the aggregate bounds of the children.
    const leftDelta = newChildBoundsUnion.left - childBoundsUnion.left;
    const topDelta = newChildBoundsUnion.top - childBoundsUnion.top;
    const rightDelta = newChildBoundsUnion.right - childBoundsUnion.right;
    const bottomDelta = newChildBoundsUnion.bottom - childBoundsUnion.bottom;
    const parentBounds = draft.nodeBounds(parent);
    const newParentBounds = parentBounds.adjustingBy(
      leftDelta,
      topDelta,
      rightDelta,
      bottomDelta
    );
    if (!newParentBounds.equals(parentBounds)) {
      draft.setNodeBounds(parent, newParentBounds);
    }
  }
}

export function layoutNode(doc: Doc, node: NID, cache: VisualCache, center?: Point) {
  doc.graph.withNodeData(node, spec => {
    const currentLayout = doc.layout.nodes.get(node);
    const newCenter = center ?? currentLayout?.center;
    let newLayout: UnitLayout | GroupLayout;
    if (spec instanceof UnitSpec) {
      newLayout = makeUnitLayout(spec, newCenter, cache, doc.locale);
    } else {
      newLayout = makeGroupLayout(
        spec as GroupSpec,
        newCenter,
        undefined,
        cache,
        doc.locale
      );
    }
    doc.layout.nodes.set(node, newLayout);
  });
}

export function makeUnitLayout(
  spec: UnitSpec,
  center: Point | undefined,
  cache: VisualCache,
  locale: LocaleId
): UnitLayout {
  // Rebuild SNORF specs from current UnitSpec so title/icon/header changes are reflected
  const baseEntity = expectDefined(spec.entitySpec);
  const entitySpec: EntitySpec = { ...baseEntity, title: spec.title };
  const snorfSpec = createEntityLayoutSpec(entitySpec);
  // Use SNORF entity layout pipeline to compute intrinsic width/height based on content
  const snorfLayout = layoutEntity(entitySpec, snorfSpec, prefs.colorMode, locale, cache);

  // Preserve existing center if it exists.
  if (center) {
    snorfLayout.center = center;
  }

  return new UnitLayout(snorfLayout);
}

export function makeGroupLayout(
  spec: GroupSpec,
  center: Point | undefined,
  size: Size | undefined,
  cache: VisualCache,
  locale: LocaleId
): GroupLayout {
  const title = spec.title.get(locale);
  const fontSizeChooser = FixedFontSizeChooser.DEFAULT;
  const minWidth = LayoutConstants.GROUP_MAX_TITLE_WIDTH;
  const maxLines = 2;
  const titleColor = getAdaptiveColor(ColorName.groupTitle);
  const measuredTitle = layoutText(
    title,
    titleColor,
    prefs.colorMode,
    { fontSizeChooser, minWidth, maxLines },
    cache.paragraphCache
  );
  const color = expectDefined(AdaptiveColor.forId(spec.color));
  let bounds: Rect;
  if (!size) {
    bounds = rect()
      .settingSize(measuredTitle.size)
      .expandingBy(LayoutConstants.GROUP_TITLE_PADDING)
      .expandingToMinSize(LayoutConstants.UNIT_MIN_SIZE);
  } else {
    bounds = rect().settingSize(size);
    // .expandingToMinSize(LayoutConstants.UNIT_MIN_SIZE);
  }
  // Preserve existing center if it exists.
  if (center !== undefined) {
    bounds = bounds.settingCenter(center);
  }
  return new GroupLayout(bounds.center, bounds.size, color, measuredTitle);
}

function layoutEdges(draft: Doc) {
  const graph = draft.graph;
  const router = new EdgeRouter();

  const nodes = topologicalSort(graph.forest);

  for (const node of nodes) {
    const bounds = draft
      .nodeBounds(node)
      .expandingBy(LayoutConstants.NODE_STROKE_WIDTH / 2);
    const parent = graph.parent(node);
    const hasChildren = graph.hasChildren(node);
    if (hasChildren) {
      router.addCompoundNode(node, bounds, 0, parent);
    } else {
      router.addLeafNode(node, bounds, LayoutConstants.NODE_CORNER_RADIUS, parent);
    }
  }

  for (const edge of graph.edges) {
    router.addEdge(edge, graph.source(edge), graph.target(edge));
  }

  const routedEdges = router.routeEdges(draft.bundleEdges);

  for (const node of graph.nodes) {
    const layout = draft.nodeLayout(node);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const bounds = layout.bounds; // Cache the bounds.
  }

  for (const edge of draft.graph.edges) {
    const path = routedEdges.get(edge);
    if (path) {
      draft.graph.withEdgeData(edge, _ => {
        path.recalculateCache();
        draft.layout.edges.set(edge, new EdgeLayout(path));
      });
    }
  }
}

export function stringIdForOrientation(orientation: LayoutOrientation): StringId {
  switch (orientation) {
    case LayoutOrientation.LEFT_TO_RIGHT:
      return StringId.LAYOUT_LEFT_TO_RIGHT;
    case LayoutOrientation.TOP_TO_BOTTOM:
      return StringId.LAYOUT_TOP_TO_BOTTOM;
    case LayoutOrientation.RIGHT_TO_LEFT:
      return StringId.LAYOUT_RIGHT_TO_LEFT;
    case LayoutOrientation.BOTTOM_TO_TOP:
      return StringId.LAYOUT_BOTTOM_TO_TOP;
    case LayoutOrientation.UNCONSTRAINED:
      return StringId.LAYOUT_UNCONSTRAINED;
  }
}

export function nameForOrientation(orientation: LayoutOrientation): string {
  return localeManager.localizedString(stringIdForOrientation(orientation));
}
